import { useEffect, useState, type FC, type PropsWithChildren } from "react";
import LogoIcon from "./icons/LogoIcon";
import MenuOpenerIcon from "./icons/MenuOpenerIcon";
import FullscreenToggleButton from "./FullscreenToggleButton";
import CloseIcon from "./icons/CloseIcon";
import { Slot, Templated, runSlotAction } from "../templating";
import { useCore } from "../contexts/useCore";
import { useTranslation } from "../contexts/useTranslation";

interface NavbarProps {
  bordered?: boolean;
  isFullscreen: boolean;
  /**
   * Suppress the login button. Set by the pages that ARE the login flow
   * (login, enter-code) — there the button is a no-op at best and discards
   * a half-entered code at worst.
   */
  hideLogin?: boolean;
}

const Navbar: FC<PropsWithChildren<NavbarProps>> = ({ children, bordered = true, isFullscreen, hideLogin = false }) => {
  const core = useCore();
  const { translate } = useTranslation();
  // The login entry point disappears once the visitor completed the
  // one-time-code flow (LAS-1286 restore). `state.contact.verified` is the
  // canonical auth flag — the same one that drives
  // `data-lastbot-authenticated` — so subscribing here keeps the button in
  // sync with a login that happened in any placement or via the headless API.
  const [verified, setVerified] = useState<boolean>(
    () => core.getState().contact?.verified ?? false,
  );
  useEffect(
    () => core.subscribe(state => setVerified(state.contact?.verified ?? false)),
    [core],
  );

  return (
    <Templated id="lastbot-navbar">
      <div className={`flex justify-between items-center px-4 h-16 ${bordered ? "border-b" : ""}`}>
        {children ? (
          // Inner pages (login, enter-code, error, customer-inquiry, history,
          // conversation) pass a back-button as `children`. Wrap it in a Slot
          // so an embedder navbar template can place it via
          // `<slot name="back-button">`. Without the Slot wrap the harvester
          // sees no slot id and the back button has nowhere to project — it
          // would disappear from any templated navbar.
          <Slot name="back-button">{children}</Slot>
        ) : (
          <div className="flex items-center gap-x-4">
            {/*
              The Slot WRAPS the entire button (with its onClick), not just
              the icon. When an embedder overrides the navbar template and
              leaves `<slot name="history-button"></slot>` empty, harvest
              projects the whole button + onClick into the placeholder; the
              click still navigates. Embedders who want their own button
              should use `<slot name="history-button"><button>X</button></slot>`
              — `wireInteractiveFallback` attaches the registry onClick to
              the embedder's button.
            */}
            <Slot name="history-button">
              <button
                type="button"
                onClick={() => runSlotAction(core, "history-button")}
                className="cursor-pointer"
                data-testid={`chat-history-button`}
              >
                <MenuOpenerIcon />
              </button>
            </Slot>
            <Slot name="brand-logo"><LogoIcon /></Slot>
          </div>
        )}
        <div className="flex items-center gap-x-2">
          {!hideLogin && !verified && (
            <Slot name="login-button">
              <button
                type="button"
                onClick={() => runSlotAction(core, "login-button")}
                className="text-sm text-lastbot-on-surface font-medium mr-0.5 p-0.5 cursor-pointer"
                data-testid={`login-button`}
              >
                {translate("login")}
              </button>
            </Slot>
          )}
          <Slot name="fullscreen-button">
            <FullscreenToggleButton
              onToggleFullscreen={() => runSlotAction(core, "fullscreen-button")}
              isFullscreen={isFullscreen}
            />
          </Slot>
          <Slot name="close-button">
            <button
              type="button"
              onClick={() => runSlotAction(core, "close-button")}
              className="-mr-1 text-lastbot-icon-muted cursor-pointer"
              data-testid={`close-button`}
            >
              <CloseIcon />
            </button>
          </Slot>
        </div>
      </div>
    </Templated>
  );
};

export default Navbar;
